import {
  applySnapshot,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import {
  createEmailCycleNameModel,
  EmailCycleNameModel,
} from "../models/EmailCycleNameModel";
import { GetEmailCycleNamesRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getEmailCycleNamesRPC";
import { getAPIClient } from "../../networking/APIClient";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import { useGetEmailCycleNamesRPCClientImpl } from "../rpcs/RPC";
import { createBoundedNameModel } from "../../models/BoundedNameModel";

const getInitialState = (): SnapshotIn<typeof EmailCycleNamesStore> => ({
  emailCycleNames: [],
  loadingEmailCycleNames: false,
  getEmailCycleNamesRPCError: null,
});

export const EmailCycleNamesStore = types
  .model("EmailCycleNamesStore", {
    emailCycleNames: types.array(EmailCycleNameModel),
    loadingEmailCycleNames: types.boolean,
    getEmailCycleNamesRPCError: types.maybeNull(
      types.enumeration(
        "GetEmailCycleNamesRPCError",
        Object.values(GetEmailCycleNamesRPC.RPCError),
      ),
    ),
  })
  .actions((store) => ({
    getEmailCycleNames: flow(function* (projectId: string) {
      store.loadingEmailCycleNames = true;
      store.getEmailCycleNamesRPCError = null;

      try {
        const apiClient = getAPIClient(store);
        const request = new GetEmailCycleNamesRPC.Request(
          new LeoUUID(projectId),
        );

        const result: LeoRPCResult<
          GetEmailCycleNamesRPC.Response,
          GetEmailCycleNamesRPC.Errors.Errors
        > =
          yield useGetEmailCycleNamesRPCClientImpl(apiClient).execute(request);

        if ("response" in result) {
          const emailCycleNames = result.response.cycles.map((cycle) =>
            createEmailCycleNameModel({
              scheduleId: cycle.scheduleId.uuid,
              name: createBoundedNameModel({
                name: cycle.cycleName.name,
              }),
            }),
          );
          store.emailCycleNames.replace(emailCycleNames);
          return;
        }

        switch (result.error.code) {
          case GetEmailCycleNamesRPC.RPCError.InvalidProjectId:
            store.getEmailCycleNamesRPCError = result.error.code;
            break;
          default:
            result.error.code satisfies never;
        }
      } finally {
        store.loadingEmailCycleNames = false;
      }
    }),
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createEmailCycleNamesStore = (): Instance<
  typeof EmailCycleNamesStore
> => EmailCycleNamesStore.create(getInitialState());
